import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import supabase from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';

const S = {
  page: { minHeight: '100vh', background: '#0f1115', padding: '24px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: 800, color: '#fff' },
  nav: { display: 'flex', gap: '8px' },
  navBtn: { background: '#2a2d35', color: '#9ca3af', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', textDecoration: 'none' },
  navBtnActive: { background: '#00e701', color: '#000' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' },
  card: { background: '#1a1d24', borderRadius: '12px', padding: '24px', border: '1px solid #2a2d35' },
  cardTitle: { fontSize: '14px', fontWeight: 700, color: '#fff', marginBottom: '20px' },
  input: { width: '100%', background: '#0f1115', border: '1px solid #2a2d35', borderRadius: '8px', color: '#fff', fontSize: '14px', padding: '12px 16px', outline: 'none', marginBottom: '12px' },
  select: { width: '100%', background: '#0f1115', border: '1px solid #2a2d35', borderRadius: '8px', color: '#fff', fontSize: '14px', padding: '12px 16px', outline: 'none', marginBottom: '12px', cursor: 'pointer' },
  btn: { width: '100%', background: '#00e701', color: '#000', border: 'none', borderRadius: '8px', padding: '12px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' },
  btnDanger: { background: '#ff4444', color: '#fff' },
  result: { marginTop: '16px', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', textAlign: 'center' },
  resultSuccess: { background: 'rgba(0,231,1,0.1)', border: '1px solid #00e701', color: '#00e701' },
  resultError: { background: 'rgba(255,68,68,0.1)', border: '1px solid #ff4444', color: '#ff4444' },
  table: { background: '#1a1d24', borderRadius: '12px', border: '1px solid #2a2d35', overflow: 'hidden', marginTop: '24px' },
  th: { background: '#0f1115', padding: '12px 16px', textAlign: 'left', fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 },
  td: { padding: '14px 16px', fontSize: '13px', color: '#fff', borderBottom: '1px solid #2a2d35' },
  statRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #2a2d35', fontSize: '13px' },
  statLabel: { color: '#6b7280' },
  statVal: { color: '#fff', fontWeight: 600 },
  historyItem: { padding: '12px 0', borderBottom: '1px solid #2a2d35' },
  historyDate: { fontSize: '11px', color: '#6b7280', marginBottom: '4px' },
  historyAction: { fontSize: '13px', color: '#fff' },
  historyAmount: { fontSize: '13px', fontWeight: 600 },
  historyGreen: { color: '#00e701' },
  historyRed: { color: '#ff4444' },
  searchResult: { background: '#0f1115', borderRadius: '8px', padding: '16px', marginBottom: '16px' },
  userInfo: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  userName: { fontSize: '16px', fontWeight: 700, color: '#fff' },
  userBalance: { fontSize: '16px', fontWeight: 700, color: '#00e701' },
  empty: { padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '13px' },
};

export default function AdminManage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [targetUser, setTargetUser] = useState(null);
  const [amount, setAmount] = useState('');
  const [action, setAction] = useState('add');
  const [reason, setReason] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/betonmines/login');
      return;
    }
  }, [user]);

  async function searchUser(query) {
    setUsername(query);
    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const { data } = await supabase
      .from('users')
      .select('id, username, balance')
      .ilike('username', `%${query}%`)
      .limit(5);

    setSearchResults(data || []);
    setShowDropdown(true);
  }

  async function selectUser(u) {
    setTargetUser(u);
    setUsername(u.username);
    setShowDropdown(false);
    
    const { data } = await supabase
      .from('balance_history')
      .select('*')
      .eq('user_id', u.id)
      .order('created_at', { ascending: false })
      .limit(20);
    
    setHistory(data || []);
  }

  async function applyChange() {
    if (!targetUser) {
      setResult({ type: 'error', message: 'Please select a user first' });
      return;
    }

    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setResult({ type: 'error', message: 'Please enter a valid amount' });
      return;
    }

    setLoading(true);
    const newBalance = action === 'add' 
      ? Number(targetUser.balance || 0) + amt
      : Math.max(0, Number(targetUser.balance || 0) - amt);

    await supabase
      .from('users')
      .update({ balance: newBalance })
      .eq('id', targetUser.id);

    await supabase.from('balance_history').insert([{
      user_id: targetUser.id,
      admin_id: user.id,
      action: action,
      amount: amt,
      balance_before: targetUser.balance,
      balance_after: newBalance,
      reason: reason || null,
    }]);

    setTargetUser({ ...targetUser, balance: newBalance });
    setAmount('');
    setReason('');
    setResult({ type: 'success', message: `Successfully ${action === 'add' ? 'added' : 'removed'} ₿${amt.toFixed(2)} ${action === 'add' ? 'to' : 'from'} ${targetUser.username}` });

    const { data } = await supabase
      .from('balance_history')
      .select('*')
      .eq('user_id', targetUser.id)
      .order('created_at', { ascending: false })
      .limit(20);
    setHistory(data || []);

    setLoading(false);
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={S.title}>⚙️ Manage Balances</div>
        <div style={S.nav}>
          <Link to="/betonmines" style={S.navBtn}>Withdrawals</Link>
          <Link to="/betonmines/users" style={S.navBtn}>Users</Link>
          <Link to="/betonmines/manage" style={{ ...S.navBtn, ...S.navBtnActive }}>Manage</Link>
        </div>
      </div>

      <div style={S.grid}>
        <div style={S.card}>
          <div style={S.cardTitle}>Add / Remove Balance</div>

          <div style={{ position: 'relative' }}>
            <input
              style={S.input}
              placeholder="Search username..."
              value={username}
              onChange={e => searchUser(e.target.value)}
            />
            {showDropdown && searchResults.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#1a1d24', border: '1px solid #2a2d35', borderRadius: '8px', zIndex: 10, marginTop: '-8px' }}>
                {searchResults.map(u => (
                  <div
                    key={u.id}
                    style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #2a2d35', display: 'flex', justifyContent: 'space-between' }}
                    onClick={() => selectUser(u)}
                  >
                    <span style={{ color: '#fff' }}>{u.username}</span>
                    <span style={{ color: '#00e701' }}>₿{Number(u.balance || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {targetUser && (
            <div style={S.searchResult}>
              <div style={S.userInfo}>
                <span style={S.userName}>@{targetUser.username}</span>
                <span style={S.userBalance}>₿{Number(targetUser.balance || 0).toFixed(2)}</span>
              </div>
            </div>
          )}

          <select style={S.select} value={action} onChange={e => setAction(e.target.value)}>
            <option value="add">Add Balance</option>
            <option value="remove">Remove Balance</option>
          </select>

          <input
            style={S.input}
            placeholder="Amount (₿)"
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={e => setAmount(e.target.value)}
          />

          <input
            style={S.input}
            placeholder="Reason (optional)"
            value={reason}
            onChange={e => setReason(e.target.value)}
          />

          <button
            style={{ ...S.btn, ...(loading ? { opacity: 0.7 } : {}), ...(action === 'remove' ? S.btnDanger : {}) }}
            onClick={applyChange}
            disabled={loading}
          >
            {loading ? 'Processing...' : `${action === 'add' ? 'Add' : 'Remove'} ₿${amount || '0'}`
            }
          </button>

          {result && (
            <div style={{ ...S.result, ...(result.type === 'success' ? S.resultSuccess : S.resultError) }}>
              {result.message}
            </div>
          )}
        </div>

        <div style={S.card}>
          <div style={S.cardTitle}>Quick Stats</div>
          <QuickStats />
        </div>
      </div>

      {targetUser && (
        <div style={S.table}>
          <div style={{ padding: '16px', borderBottom: '1px solid #2a2d35' }}>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>
              Balance History: @{targetUser.username}
            </span>
          </div>
          {history.length === 0 && <div style={S.empty}>No balance changes yet</div>}
          {history.map(h => (
            <div key={h.id} style={S.historyItem}>
              <div style={S.historyDate}>{new Date(h.created_at).toLocaleString()}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={S.historyAction}>
                  {h.action === 'add' ? 'Added' : 'Removed'} ₿{Number(h.amount).toFixed(2)}
                  {h.reason && <span style={{ color: '#6b7280', marginLeft: '8px' }}>({h.reason})</span>}
                </span>
                <span style={{ ...S.historyAmount, ...(h.action === 'add' ? S.historyGreen : S.historyRed) }}>
                  {h.action === 'add' ? '+' : '-'}₿{Number(h.amount).toFixed(2)}
                </span>
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                Balance: ₿{Number(h.balance_before).toFixed(2)} → ₿{Number(h.balance_after).toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuickStats() {
  const [stats, setStats] = useState({ totalUsers: 0, totalBalance: 0, totalGames: 0 });

  useEffect(() => {
    async function load() {
      const { count: users } = await supabase.from('users').select('*', { count: 'exact', head: true });
      const { data: allUsers } = await supabase.from('users').select('balance');
      const { count: games } = await supabase.from('games').select('*', { count: 'exact', head: true });

      setStats({
        totalUsers: users || 0,
        totalBalance: (allUsers || []).reduce((s, u) => s + Number(u.balance || 0), 0),
        totalGames: games || 0,
      });
    }
    load();
  }, []);

  return (
    <>
      <div style={S.statRow}>
        <span style={S.statLabel}>Total Users</span>
        <span style={S.statVal}>{stats.totalUsers}</span>
      </div>
      <div style={S.statRow}>
        <span style={S.statLabel}>Total Platform Balance</span>
        <span style={S.statVal}>₿{stats.totalBalance.toFixed(2)}</span>
      </div>
      <div style={S.statRow}>
        <span style={S.statLabel}>Total Games Played</span>
        <span style={S.statVal}>{stats.totalGames}</span>
      </div>
    </>
  );
}
